import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, TextField, IconButton, Typography, Chip, CircularProgress, Tooltip } from '@mui/material';
import { Send as SendIcon, AttachFile as AttachIcon, Close as CloseIcon } from '@mui/icons-material';
import MessageBubble, { type ChatMessage } from './MessageBubble';

export interface PolicyChatPanelProps {
  sendMessage: (message: string) => Promise<void>;
  sendMessageWithDocument: (message: string, files: File[]) => Promise<void>;
  isStreaming: boolean;
  streamingText: string;
  error: string | null;
  lastTokenUsage: { input: number; output: number } | null;
  messages: ChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
}

const PolicyChatPanel: React.FC<PolicyChatPanelProps> = ({
  sendMessage,
  sendMessageWithDocument,
  isStreaming,
  streamingText,
  error,
  lastTokenUsage,
  messages,
  setMessages,
}) => {
  const [input, setInput] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Scroll to bottom when messages change or streaming text updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]);

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed && pendingFiles.length === 0) return;
    if (isStreaming) return;

    const fileNames = pendingFiles.map(f => f.name);
    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content: trimmed || `[Attached: ${fileNames.join(', ')}]`,
      fileNames: fileNames.length > 0 ? fileNames : undefined,
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    const files = [...pendingFiles];
    setPendingFiles([]);

    if (files.length > 0) {
      await sendMessageWithDocument(trimmed || 'Please review the attached documents.', files);
    } else {
      await sendMessage(trimmed);
    }
  }, [input, pendingFiles, isStreaming, sendMessage, sendMessageWithDocument]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;
    setPendingFiles(prev => {
      const existingNames = new Set(prev.map(f => f.name));
      const newFiles = selected.filter(f => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });
    e.target.value = '';
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Messages Area */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}>
        {messages.length === 0 && !isStreaming && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Send a message or attach a document to start a policy review conversation.
            </Typography>
          </Box>
        )}

        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}

        {/* Streaming assistant message */}
        {isStreaming && (
          <MessageBubble
            message={{
              id: 'streaming',
              role: 'assistant',
              content: streamingText || '',
            }}
            isStreaming
          />
        )}

        {/* Error display */}
        {error && (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Token usage */}
      {lastTokenUsage && (
        <Box sx={{ px: 2, pb: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="caption" color="text.secondary">
            Tokens: {lastTokenUsage.input} in / {lastTokenUsage.output} out
          </Typography>
        </Box>
      )}

      {/* Pending file chips */}
      {pendingFiles.length > 0 && (
        <Box sx={{ px: 2, pb: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {pendingFiles.map((file) => (
            <Chip
              key={file.name}
              label={file.name}
              size="small"
              onDelete={() => setPendingFiles(prev => prev.filter(f => f.name !== file.name))}
              deleteIcon={<CloseIcon />}
              sx={{ maxWidth: '100%' }}
            />
          ))}
        </Box>
      )}

      {/* Input Area */}
      <Box sx={{
        p: 2,
        pt: 1,
        borderTop: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
      }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          hidden
          multiple
          onChange={handleFileSelect}
        />
        <Tooltip title="Attach document">
          <IconButton
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming}
          >
            <AttachIcon />
          </IconButton>
        </Tooltip>

        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Ask about a policy..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={isStreaming}
        />

        <Tooltip title="Send">
          <span>
            <IconButton
              color="primary"
              onClick={handleSend}
              disabled={isStreaming || (!input.trim() && pendingFiles.length === 0)}
            >
              {isStreaming ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default PolicyChatPanel;
